import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useToast } from '@/shared/components/ui/Toast';
import { useUpdateManufacturer } from '../hooks/useAdmin';
import type { Manufacturer } from '../services/manufacturersService';

interface BrandEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  manufacturer: Manufacturer | null;
}

export function BrandEditModal({ isOpen, onClose, manufacturer }: BrandEditModalProps) {
  const { showToast } = useToast();
  const [brands, setBrands] = useState<string[]>([]);
  const [newBrand, setNewBrand] = useState('');
  const [editingBrand, setEditingBrand] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const updateMutation = useUpdateManufacturer();

  useEffect(() => {
    if (isOpen && manufacturer) {
      setBrands(manufacturer.brands || []);
      setNewBrand('');
      setEditingBrand(null);
      setEditingValue('');
    }
  }, [isOpen, manufacturer]);

  const handleAddBrand = () => {
    const trimmed = newBrand.trim();
    if (trimmed && !brands.includes(trimmed)) {
      setBrands([...brands, trimmed]);
      setNewBrand('');
    }
  };

  const handleRemoveBrand = (brandToRemove: string) => {
    setBrands(brands.filter((b: string) => b !== brandToRemove));
    if (editingBrand === brandToRemove) {
      setEditingBrand(null);
      setEditingValue('');
    }
  };

  const handleStartEdit = (brand: string) => {
    setEditingBrand(brand);
    setEditingValue(brand);
  };

  const handleSaveEdit = () => {
    const trimmed = editingValue.trim();
    if (!trimmed) {
      showToast('Brand name cannot be empty', 'error');
      return;
    }

    if (trimmed !== editingBrand && brands.includes(trimmed)) {
      showToast('A brand with this name already exists', 'error');
      return;
    }

    setBrands(brands.map((b) => (b === editingBrand ? trimmed : b)));
    setEditingBrand(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingBrand(null);
    setEditingValue('');
  };

  const handleEditKeyPress = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleSave = async () => {
    if (!manufacturer) return;

    try {
      await updateMutation.mutateAsync({
        id: manufacturer.id,
        brands: brands.length > 0 ? brands : undefined,
      });
      showToast('Brands updated successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error updating brands:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to update brands',
        'error'
      );
    }
  };

  const handleKeyPress = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddBrand();
    }
  };

  if (!manufacturer) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit Brands - ${manufacturer.name}`}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Brands
          </label>
          {brands.length > 0 ? (
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg min-h-[60px]">
              {brands.map((brand: string) => (
                <div key={brand}>
                  {editingBrand === brand ? (
                    <div className="inline-flex items-center gap-1 bg-white border border-blue-500 rounded px-2 py-1">
                      <Input
                        value={editingValue}
                        onChange={(e: { target: { value: string } }) => setEditingValue(e.target.value)}
                        onKeyPress={handleEditKeyPress}
                        className="w-32 h-6 text-sm px-2 py-0"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="text-green-600 hover:text-green-700 font-bold"
                        aria-label="Save"
                        title="Save (Enter)"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="text-red-600 hover:text-red-700 font-bold"
                        aria-label="Cancel"
                        title="Cancel (Esc)"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <Badge
                      variant="neutral"
                      className="text-sm flex items-center gap-2 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleStartEdit(brand)}
                      title="Click to edit"
                    >
                      {brand}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveBrand(brand);
                        }}
                        className="ml-1 text-gray-500 hover:text-red-600 transition-colors"
                        aria-label={`Remove ${brand}`}
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500 text-center">
              No brands added yet
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add New Brand
          </label>
          <div className="flex gap-2">
            <Input
              value={newBrand}
              onChange={(e: { target: { value: string } }) => setNewBrand(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter brand name"
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleAddBrand}
              disabled={!newBrand.trim() || brands.includes(newBrand.trim())}
            >
              Add
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Press Enter or click Add to add a brand. Click on any brand to edit its name.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
